import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { VendingMachineService, SuburbInfo } from '../../api/v1/api_pb';
import { useClient } from '../../connect_api';
import { VendingMachineInfo } from '../../api/v1/api_pb';
import { Helmet } from 'react-helmet';
import logo from '../../img/logo/mytest-white-logo.png';
import { haversineDistance, formatDistance } from '../../utils/geo';

interface VendingMachineWithDistance extends VendingMachineInfo {
    distanceKm?: number;
}

export default function VendingMachinePageRedesigned() {
    const [vendingMachineInformations, setVendingMachineInformations] = useState<VendingMachineWithDistance[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [suburbSuggestions, setSuburbSuggestions] = useState<SuburbInfo[]>([]);
    const [selectedSuburb, setSelectedSuburb] = useState<SuburbInfo | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const client = useClient(VendingMachineService);
    const navigate = useNavigate();

    const getAllVendingMachine = useCallback(async () => {
        try {
            const response = await client.listVendingMachines({});
            const machines = response.vendingMachines;

            // If a suburb is selected, calculate distances and sort
            if (selectedSuburb) {
                const machinesWithDistance = machines.map((machine) => ({
                    ...machine,
                    distanceKm: haversineDistance(
                        selectedSuburb.latitude,
                        selectedSuburb.longitude,
                        machine.latitude,
                        machine.longitude
                    ),
                }));

                // Sort by distance (closest first)
                machinesWithDistance.sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0));

                setVendingMachineInformations(machinesWithDistance);
            } else {
                setVendingMachineInformations(machines);
            }
        } catch (error) {
            navigate(`/`, { replace: true });
        }
    }, [client, navigate, selectedSuburb]);

    useEffect(() => {
        getAllVendingMachine();
    }, [getAllVendingMachine]);

    // Search suburbs with typeahead
    const searchSuburbs = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSuburbSuggestions([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        try {
            const response = await client.searchSuburbs({ query, limit: 10 });
            setSuburbSuggestions(response.suburbs);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Failed to search suburbs:', error);
            setSuburbSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    }, [client]);

    // Handle search query change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        searchSuburbs(query);
    };

    // Handle suburb selection
    const handleSuburbSelect = (suburb: SuburbInfo) => {
        setSelectedSuburb(suburb);
        setSearchQuery(`${suburb.locality} ${suburb.postcode}`);
        setShowSuggestions(false);
    };

    // Handle input blur with delay to allow clicks on suggestions
    const handleInputBlur = () => {
        // Delay hiding to allow click events on suggestions to fire first
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200);
    };

    const handleCollectFromHere = (vendingMachine: VendingMachineInfo) => {
        navigate('/machine-detail/' + vendingMachine.machineCode);
    };

    const handleShowOnMap = (vendingMachine: VendingMachineInfo) => {
        // Use URL parameters instead of navigation state (iOS Safari loses state)
        navigate(`/maps?machine=${encodeURIComponent(vendingMachine.machineCode)}`);
    };

    const handleGoBack = () => {
        navigate('/');
    };

    // Get formatted distance
    const getDistance = (vendingMachine: VendingMachineWithDistance): string => {
        if (vendingMachine.distanceKm !== undefined) {
            return formatDistance(vendingMachine.distanceKm);
        }
        return '';
    };

    return (
        <>
            <Helmet>
                <title>Find a MyTest vending machine</title>
            </Helmet>
            <div className="vending-machine-list-page">
                <img src={logo} alt="MyTest Logo" className="logo" />

                <div className="headline">
                    <span className="back-link" onClick={handleGoBack}>&lt; Back</span>
                    <h1 className="page-title">Find a MyTest vending machine</h1>
                    <p className="page-subtitle">Where would you like to collect your MyTest self-test kit?</p>
                </div>

                <p className="scroll-hint">To find nearby machines, enter your suburb or postcode</p>

                <div className="search-section">
                    <div className="search-input-wrapper">
                        <div className="search-input">
                            {isSearching ? (
                                <svg className="search-icon spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="8" cy="8" r="6" stroke="#400093" strokeWidth="2" strokeLinecap="round" strokeDasharray="30 10" />
                                </svg>
                            ) : (
                                <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z" stroke="#400093" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M15 15L11.5 11.5" stroke="#400093" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={handleInputBlur}
                                placeholder="e.g. Parramatta or 2150"
                            />
                        </div>
                        {showSuggestions && suburbSuggestions.length > 0 && (
                            <div className="suburb-suggestions">
                                {suburbSuggestions.map((suburb, index) => (
                                    <div
                                        key={index}
                                        className="suburb-suggestion-item"
                                        onClick={() => handleSuburbSelect(suburb)}
                                    >
                                        <strong>{suburb.locality}</strong> {suburb.postcode}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedSuburb && (
                        <p className="scroll-hint">
                            Showing machines near {selectedSuburb.locality} {selectedSuburb.postcode}
                        </p>
                    )}
                </div>

                <div className="locations-list">
                    {vendingMachineInformations.map((vendingMachine, index) => (
                        <div key={index} className="location-card">
                            <div className="location-header">
                                <h2 className="location-name">{vendingMachine.name}</h2>
                                <p className="location-distance">{getDistance(vendingMachine)}</p>
                            </div>

                            <div className="location-details">
                                <p>{vendingMachine.address}</p>
                                <p>Machine location: {vendingMachine.locationName}</p>
                                <p>Machine hours: {vendingMachine.openingHours}</p>
                            </div>

                            <button
                                className="collect-button"
                                onClick={() => handleCollectFromHere(vendingMachine)}
                            >
                                Collect from here
                            </button>

                            <button
                                className="show-map-link"
                                onClick={() => handleShowOnMap(vendingMachine)}
                            >
                                Show on map
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}
